import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, Clock } from "lucide-react";
import SEO from "../components/SEO";
import { API } from "../lib/api";

const POLL_MS = 2000;
const TIMEOUT_MS = 60000;

export default function BookingSuccessPage() {
  const [phase, setPhase] = useState("confirming"); // confirming | active | timeout | no-id
  const [info, setInfo] = useState(null);
  const stopRef = useRef(false);

  useEffect(() => {
    let enrollmentId = "";
    let title = "";
    try {
      enrollmentId = sessionStorage.getItem("nexora_enrollment_id") || "";
      title = sessionStorage.getItem("nexora_booking_title") || "";
    } catch {
      /* ignore */
    }
    if (title) setInfo((i) => ({ ...(i || {}), schedule_title: title }));
    if (!enrollmentId) {
      setPhase("no-id");
      return;
    }

    const started = Date.now();
    const poll = async () => {
      if (stopRef.current) return;
      try {
        const res = await fetch(`${API}/site/booking-status/${enrollmentId}`);
        if (res.ok) {
          const d = await res.json();
          if (d.status === "active") {
            setInfo(d);
            setPhase("active");
            return;
          }
        }
      } catch {
        /* transient — keep polling */
      }
      if (Date.now() - started >= TIMEOUT_MS) {
        setPhase("timeout");
        return;
      }
      setTimeout(poll, POLL_MS);
    };
    poll();
    return () => {
      stopRef.current = true;
    };
  }, []);

  return (
    <>
      <SEO path="/booking/success" title="Booking Confirmation | Nexora" description="Nexora booking confirmation." noindex />
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-20 lg:py-28" data-testid="booking-success-page">
        <div className="rounded-[2rem] bg-white border border-slate-200 p-8 sm:p-12 text-center shadow-sm">
          {phase === "confirming" && (
            <div data-testid="booking-success-confirming">
              <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#EAF7FB]">
                <Loader2 className="h-8 w-8 animate-spin text-[#00A6B2]" />
              </span>
              <h1 className="mt-6 font-display text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: "#123B6D" }}>
                Payment received — confirming your spot…
              </h1>
              <p className="mt-3 text-sm text-slate-600">
                This usually takes a few seconds. Please don't close this page.
              </p>
            </div>
          )}

          {phase === "active" && (
            <div data-testid="booking-success-confirmed">
              <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
                <CheckCircle2 className="h-9 w-9 text-emerald-500" />
              </span>
              <h1 className="mt-6 font-display text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: "#123B6D" }}>
                {info?.child_name ? `Spot confirmed for ${info.child_name}!` : "Your spot is confirmed!"}
              </h1>
              {info?.schedule_title && (
                <p className="mt-3 text-base font-semibold text-slate-800" data-testid="booking-success-class">
                  {info.schedule_title}
                </p>
              )}
              {info?.next_billing_date && (
                <p className="mt-1.5 text-sm text-slate-600" data-testid="booking-success-next-billing">
                  Next billing date: {info.next_billing_date}
                </p>
              )}
              <p className="mt-4 text-sm text-slate-600">
                A confirmation email is on its way. Questions? Contact us at{" "}
                <a href="mailto:admissions@nexoracentre.ca" className="font-semibold text-[#0369A1]">admissions@nexoracentre.ca</a>{" "}
                or <a href="tel:+16046555306" className="font-semibold text-[#0369A1]">604-655-5306</a>.
              </p>
            </div>
          )}

          {(phase === "timeout" || phase === "no-id") && (
            <div data-testid="booking-success-pending">
              <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#FFF1E8]">
                <Clock className="h-8 w-8 text-[#FF8C42]" />
              </span>
              <h1 className="mt-6 font-display text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: "#123B6D" }}>
                Payment received — confirmation is processing.
              </h1>
              <p className="mt-3 text-sm text-slate-600 max-w-md mx-auto">
                Your payment went through and your spot is being confirmed. You'll receive a
                confirmation email shortly. If you don't hear from us soon, please contact{" "}
                <a href="mailto:admissions@nexoracentre.ca" className="font-semibold text-[#0369A1]">admissions@nexoracentre.ca</a>{" "}
                or <a href="tel:+16046555306" className="font-semibold text-[#0369A1]">604-655-5306</a>.
              </p>
            </div>
          )}

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/classes"
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold border-2"
              style={{ borderColor: "#00A6B2", color: "#00A6B2" }}
              data-testid="booking-success-back-btn"
            >
              Back to classes
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-white"
              style={{ backgroundColor: "#123B6D" }}
            >
              Go to homepage
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
